import React, { useState, useEffect } from "react";
import * as ort from "onnxruntime-web";

const LicensePlateDetect = ({ base64Image, onDetect }) => {
    const [croppedPlate, setCroppedPlate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load the YOLO model with better error handling
    const loadModel = async () => {
        try {
            // console.log("Loading ONNX model...");

            // Try multiple potential paths
            const modelPaths = [
                "/models/yolo-v9-s-608-license-plates-end2end.onnx",
                "./models/yolo-v9-s-608-license-plates-end2end.onnx",
                "../../../models/yolo-v9-s-608-license-plates-end2end.onnx",
            ];

            let session = null;
            let lastError = null;

            for (const path of modelPaths) {
                try {
                    // console.log(`Trying to load model from: ${path}`);
                    session = await ort.InferenceSession.create(path, {
                        executionProviders: ["wasm"],
                    });
                    // console.log("Model loaded successfully from:", path);
                    break;
                } catch (err) {
                    console.warn(`Failed to load from ${path}:`, err.message);
                    lastError = err;
                }
            }

            if (!session) {
                throw (
                    lastError || new Error("Could not load model from any path")
                );
            }

            return session;
        } catch (error) {
            console.error("Error loading model:", error);
            throw error;
        }
    };

    // Preprocess the image (resize and normalize)
    const preprocess = (image, size) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = size;
        canvas.height = size;
        ctx.filter = "brightness(1.2) contrast(1.2)";
        ctx.drawImage(image, 0, 0, size, size);

        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;

        // YOLO typically expects [batch, channels, height, width] format
        const tensorData = new Float32Array(3 * size * size);

        for (let i = 0; i < size * size; i++) {
            const pixelIndex = i * 4;
            // Normalize to [0, 1] and arrange in CHW format
            tensorData[i] = data[pixelIndex] / 255.0; // Red channel
            tensorData[size * size + i] = data[pixelIndex + 1] / 255.0; // Green channel
            tensorData[2 * size * size + i] = data[pixelIndex + 2] / 255.0; // Blue channel
        }

        return new ort.Tensor("float32", tensorData, [1, 3, size, size]);
    };

    const postprocess = (outputs, imageWidth, imageHeight, inputSize = 608) => {
        const output = outputs[Object.keys(outputs)[0]];
        const data = output.data;
        const dims = output.dims;

        const boxes = [];
        const numDetections = dims[0]; // 1
        const valuesPerDetection = dims[1]; // 7

        for (let i = 0; i < numDetections; i++) {
            const offset = i * valuesPerDetection;

            const x1 = (data[offset + 1] / inputSize) * imageWidth;
            const y1 = (data[offset + 2] / inputSize) * imageHeight;
            const x2 = (data[offset + 3] / inputSize) * imageWidth;
            const y2 = (data[offset + 4] / inputSize) * imageHeight;
            const confidence = data[offset + 6];

            if (confidence > 0.3) {
                boxes.push({
                    x: x1,
                    y: y1,
                    width: x2 - x1,
                    height: y2 - y1,
                    score: confidence,
                });
            }
        }

        return boxes;
    };

    // Crop the detected license plate from the image
    const cropPlate = (image, box) => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, box.width);
        canvas.height = Math.max(1, box.height);
        const ctx = canvas.getContext("2d");

        ctx.drawImage(
            image,
            Math.max(0, box.x),
            Math.max(0, box.y),
            box.width,
            box.height,
            0,
            0,
            box.width,
            box.height
        );

        return canvas.toDataURL();
    };

    // Main function to detect and crop the plate
    const detectAndCropPlate = async (imageElement) => {
        try {
            setLoading(true);
            setError(null);

            const model = await loadModel();
            // console.log("Model loaded successfully");

            const size = 608;
            const tensor = preprocess(imageElement, size);
            // console.log("Image preprocessed, tensor shape:", tensor.dims);

            const feeds = {};
            // Get input name from model (might not be 'input')
            const inputNames = model.inputNames;
            // console.log("Model input names:", inputNames);
            feeds[inputNames[0]] = tensor;

            const outputs = await model.run(feeds);
            // console.log("Output tensor info:", outputs);
            const output = outputs[Object.keys(outputs)[0]];
            // console.log("Dims:", output.dims);
            // console.log("Data sample:", output.data.slice(0, 20));
            // console.log("Inference completed");

            const boxes = postprocess(
                outputs,
                imageElement.width,
                imageElement.height,
                size
            );

            if (boxes.length > 0) {
                const croppedPlate = cropPlate(imageElement, boxes[0]);
                return croppedPlate;
            } else {
                // console.log("No license plates detected");
                return null;
            }
        } catch (error) {
            console.error("Error in detectAndCropPlate:", error);
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Convert base64 image to Image object and run detection
    // useEffect(() => {
    //     if (!base64Image) return;

    //     const image = new Image();
    //     image.src = base64Image;

    //     image.onload = async () => {
    //         console.log("Image loaded, starting detection...");
    //         const croppedPlate = await detectAndCropPlate(image);
    //         setCroppedPlate(croppedPlate);
    //     };

    //     image.onerror = (err) => {
    //         console.error("Error loading image:", err);
    //         setError("Failed to load image");
    //     };
    // }, [base64Image]);

    useEffect(() => {
        let didCancel = false;

        if (!base64Image) {
            onDetect(null);
            return;
        }

        const image = new Image();
        image.src = base64Image;

        image.onload = async () => {
            const result = await detectAndCropPlate(image);
            if (!didCancel) {
                onDetect(result || null); // make sure to return null if result is falsy
            }
        };

        image.onerror = () => {
            console.error("Image failed to load");
            if (!didCancel) {
                onDetect(null);
            }
        };

        return () => {
            didCancel = true;
        };
    }, [base64Image]);
    return null;
};

export default LicensePlateDetect;
